import { Component, OnInit, OnDestroy } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { Location } from '@angular/common'
import { ActivatedRoute } from "@angular/router";
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { startOfToday, startOfTomorrow, isToday, isTomorrow, isMatch, parseISO, formatISO, startOfMonth, endOfMonth, subMonths } from 'date-fns'
import { saveAs } from 'file-saver'
import { Subscription } from 'rxjs'

import { AlertService } from '@/services/alert.service'
import { AuthenticationService } from '@/services/authentication.service'
import { CableService } from '@/services/cable.service'
import { SalesPeriodsService } from '@/services/sales-periods.service'
import { Store } from '@/domain/store';

@UntilDestroy()
@Component({
  selector: 'merchant-dashboard-view',
  templateUrl: 'merchant-dashboard-view.component.html'
  // styleUrls: [ 'merchant-dashboard-view.component.scss' ]
})
export class MerchantDashboardViewComponent implements OnInit, OnDestroy {

  salesSubscription: Subscription

  day1: Date = startOfToday()
  day2: Date = startOfTomorrow()

  storeSelection: Store = null

  salesReportFrom: NgbDateStruct = this.asNgbDateStruct(startOfMonth(subMonths(new Date(), 1)))
  salesReportTo: NgbDateStruct = this.asNgbDateStruct(endOfMonth(subMonths(new Date(), 1)))
  // True while the range report is in flight: spinner on the button, buttons disabled.
  downloadingSales: boolean = false

  constructor(
    public salesPeriodsService: SalesPeriodsService,
    public auth: AuthenticationService,
    private _route: ActivatedRoute,
    private location: Location,
    private alertService: AlertService,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    // this.salesSubscription = this._cableService.sales.subscribe(sale => {
    //   console.log('received sale', sale)
    // })

    this._route.params.pipe(untilDestroyed(this)).subscribe(params => {
      if (params['dates']) {
        const dates = params['dates'].split(',')
        if (dates.length > 0) {
          if (dates[0] === 'today') {
            this.day1 = startOfToday()
          } else if (isMatch(dates[0], 'yyyy-MM-dd')) {
            this.day1 = parseISO(dates[0])
          }
        }
        if (dates.length > 1) {
          if (dates[1] === 'tomorrow') {
            this.day2 = startOfTomorrow()
          } else if (isMatch(dates[1], 'yyyy-MM-dd')) {
            this.day2 = parseISO(dates[1])
          }
        }
      }
      var storeId = this.loadStoreChoice()
      const user = this.auth.currentUserValue
      if (storeId && user?.merchant?.stores) {
        let stores = user.merchant.stores
        this.storeSelection = stores.find(s => s.id == storeId)
      } else {
        this.storeSelection = null
      }
    })
  }

  ngOnDestroy() {
    // this.salesSubscription.unsubscribe()
  }

  day1Changed(date: Date): void {
    this.day1 = date
    this.updateLocation()
  }

  day2Changed(date: Date): void {
    this.day2 = date
    this.updateLocation()
  }

  getDailySalesReport(when: string): void {
    this.salesPeriodsService.getDailySalesReport(when).subscribe(
      blob => {
        saveAs(blob, `daily-sales.xlsx`)
      },
      error => {})
  }

  get canDownloadSalesReport(): boolean {
    let from = this.formatDate(this.salesReportFrom)
    let to = this.formatDate(this.salesReportTo)
    return !!from && !!to && to >= from
  }

  downloadSalesReport(): void {
    let from = this.formatDate(this.salesReportFrom)
    let to = this.formatDate(this.salesReportTo)
    if (!from || !to) {
      this.alertService.error(this.translate.instant('common.report_range_error'))
      return
    }
    this.downloadingSales = true
    this.salesPeriodsService.getSalesReport(from, to).subscribe(
      blob => {
        this.downloadingSales = false
        saveAs(blob, `myynnit-${from}-${to}.xlsx`)
      },
      (error: HttpErrorResponse) => {
        this.downloadingSales = false
        if (error.status === 400) {
          this.alertService.error(this.translate.instant('common.report_range_error'))
        }
      })
  }

  selectedStore(store: Store): void {
    this.storeSelection = store
    if (store) {
      this.saveStoreChoice(store.id)
    } else {
      this.clearStoreChoice()
    }
  }

  private loadStoreChoice(): number {
    var id = sessionStorage.getItem('dashboard.storeId')
    return id ? +id : null
  }

  private saveStoreChoice(id: number): void {
    sessionStorage.setItem('dashboard.storeId', ''+id)
  }

  private clearStoreChoice(): void {
    sessionStorage.removeItem('dashboard.storeId')
  }

  // ngbDatepicker hands back an NgbDateStruct when a day is picked or the typed text parses;
  // when the typed text does not parse it leaves the raw string in the model. Accept both
  // yyyy-mm-dd and the Finnish d.m.yyyy; null means "not a date".
  private formatDate(date: NgbDateStruct | string | null): string | null {
    if (!date) {
      return null
    }
    let y: number, m: number, d: number
    if (typeof date === 'string') {
      let iso = date.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
      let fi = date.trim().match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
      if (iso) { y = +iso[1]; m = +iso[2]; d = +iso[3] }
      else if (fi) { y = +fi[3]; m = +fi[2]; d = +fi[1] }
      else { return null }
    } else {
      y = date.year; m = date.month; d = date.day
    }
    if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) {
      return null
    }
    return `${y}-${(""+m).padStart(2, '0')}-${(""+d).padStart(2, '0')}`
  }

  private asNgbDateStruct(date: Date): NgbDateStruct {
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate() }
  }

  private updateLocation(): void {
    let second = 'tomorrow'
    if (!isTomorrow(this.day2)) {
      second = formatISO(this.day2, { representation: 'date' })
    }
    let first = 'today'
    if (!isToday(this.day1)) {
      first = formatISO(this.day1, { representation: 'date' })
    }
    const dateRoute = first + ',' + second
    const path = dateRoute === 'today,tomorrow' ? '' : dateRoute
    this.location.go('dashboard/' + path)
  }
}